import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { STATUS_LABELS, SHIPMENT_STATUS_LABELS } from '../../lib/constants';
import PackageStatusBadge from '../../components/PackageStatusBadge';
import { CountryRoute } from '../../components/CountryDisplay';
import ProfessionalLayout from '../../components/ProfessionalLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Ship, Package, Calendar, MapPin, AlertCircle, Clock, Search, ChevronDown, ChevronUp, Weight, User, Users, ImageIcon, Megaphone, RefreshCw, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import PackagePhotosDialog from '../../components/PackagePhotosDialog';
import ShipmentBroadcastDialog from '../../components/ShipmentBroadcastDialog';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { getShipmentETA } from '../../lib/utils';
import { useToast } from '../../hooks/use-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import SEOHead from '../../components/SEOHead';

// Transitions autorisées pour changement manuel de statut carréaison
const SHIPMENT_TRANSITIONS = {
  scheduled: ['in_transit', 'cancelled'],
  in_transit: ['arrived', 'delayed', 'cancelled'],
  delayed: ['arrived', 'in_transit', 'cancelled'],
  arrived: ['completed'],
};

// Transitions autorisées pour changement manuel de statut colis
const PACKAGE_TRANSITIONS = {
  registered: ['collected'],
  collected: ['in_transit'],
  in_transit: ['arrived'],
  arrived: ['awaiting_customs'],
  awaiting_customs: ['customs_cleared'],
  customs_cleared: ['delivered'],
  delivered: [],
};

// Tous les statuts colis (pour admin override)
const ALL_PACKAGE_STATUSES = [
  'registered', 'collected', 'in_transit', 'arrived',
  'awaiting_customs', 'customs_cleared', 'delivered'
];

// Statuts considérés comme terminés : les départs correspondants
// quittent la vue active et sont regroupés dans l'onglet "Historique".
const HISTORY_STATUSES = ['completed', 'cancelled'];

const AdminShipmentView = () => {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [shipmentsData, setShipmentsData] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [packageSearchQuery, setPackageSearchQuery] = useState('');
  const [expandedShipments, setExpandedShipments] = useState(new Set());
  const [selectedPartnerFilter, setSelectedPartnerFilter] = useState('all');
  const [photosDialog, setPhotosDialog] = useState({ open: false, packageId: null, oceCode: '' });
  const [broadcastDialog, setBroadcastDialog] = useState({ open: false, shipmentId: null, shipmentName: '' });
  const [shipmentStatusDialog, setShipmentStatusDialog] = useState({ open: false, shipmentId: null, shipmentName: '', currentStatus: '', newStatus: '', note: '' });
  const [packageStatusDialog, setPackageStatusDialog] = useState({ open: false, packageId: null, oceCode: '', currentStatus: '', newStatus: '', note: '' });
  const [statusChanging, setStatusChanging] = useState(false);
  const [customsDialog, setCustomsDialog] = useState({ open: false, packageId: null, oceCode: '', note: '' });
  const [customsBulkDialog, setCustomsBulkDialog] = useState({ open: false, shipmentId: null, shipmentName: '', packageIds: [], note: '' });

  // ETA countdown auto-refresh
  const [, setEtaTick] = useState(0);

  useEffect(() => {
    fetchShipmentsData();
  }, []);

  // Auto-refresh on WebSocket shipment_status_updated event
  useEffect(() => {
    const handler = () => fetchShipmentsData();
    window.addEventListener('shipment_status_updated', handler);
    return () => window.removeEventListener('shipment_status_updated', handler);
  }, []);

  // ETA countdown refresh every 60s
  useEffect(() => {
    const interval = setInterval(() => setEtaTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (packageSearchQuery.length >= 3) {
      searchPackages();
    } else {
      setSearchResults([]);
    }
  }, [packageSearchQuery]);

  const fetchShipmentsData = async () => {
    try {
      const response = await api.get('/api/shipments/detailed-view');
      setShipmentsData(response.data);
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Error fetching shipments data:', err);
      if (err.response?.status === 401) {
        setError(t('sessionExpired'));
      } else if (err.response?.status === 403) {
        setError(`${t('accessDenied')} - ${t('insufficientPermissions')}`);
      } else {
        setError(`${t('errorLoading')}: ${err.response?.data?.detail || err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const searchPackages = async () => {
    try {
      const response = await api.get('/api/packages/search', {
        params: { query: packageSearchQuery }
      });
      setSearchResults(response.data.results);
    } catch (err) {
      console.error('Error searching packages:', err);
      setSearchResults([]);
    }
  };

  const handleShipmentStatusChange = async () => {
    if (!shipmentStatusDialog.newStatus || !shipmentStatusDialog.note.trim()) return;
    const selectedStatus = shipmentStatusDialog.newStatus;
    setStatusChanging(true);
    try {
      await api.put(`/api/shipments/${shipmentStatusDialog.shipmentId}`, {
        status: selectedStatus,
        status_change_note: shipmentStatusDialog.note.trim()
      });
      setShipmentStatusDialog({ open: false, shipmentId: null, shipmentName: '', currentStatus: '', newStatus: '', note: '' });
      toast({ title: t('statusChanged'), description: `${(SHIPMENT_STATUS_LABELS[language] || SHIPMENT_STATUS_LABELS.fr)[selectedStatus] || selectedStatus}` });
      fetchShipmentsData();
    } catch (err) {
      console.error('Error changing shipment status:', err);
      toast({ title: t('error'), description: err.response?.data?.detail || t('errorStatusChange'), variant: 'destructive' });
    } finally {
      setStatusChanging(false);
    }
  };

  const handlePackageStatusChange = async () => {
    if (!packageStatusDialog.newStatus || !packageStatusDialog.note.trim()) return;
    const selectedStatus = packageStatusDialog.newStatus;
    const oceCode = packageStatusDialog.oceCode;
    setStatusChanging(true);
    try {
      await api.put(`/api/packages/${packageStatusDialog.packageId}/status`, {
        status: selectedStatus,
        note: packageStatusDialog.note.trim()
      });
      setPackageStatusDialog({ open: false, packageId: null, oceCode: '', currentStatus: '', newStatus: '', note: '' });
      toast({ title: t('statusChanged'), description: `${oceCode} → ${(STATUS_LABELS[language] || STATUS_LABELS.fr)[selectedStatus] || selectedStatus}` });
      fetchShipmentsData();
    } catch (err) {
      console.error('Error changing package status:', err);
      toast({ title: t('error'), description: err.response?.data?.detail || t('errorStatusChange'), variant: 'destructive' });
    } finally {
      setStatusChanging(false);
    }
  };

  const handleCustomsClearSingle = async () => {
    setStatusChanging(true);
    try {
      await api.put(`/api/packages/${customsDialog.packageId}/mark-customs-cleared`, null, {
        params: { note: customsDialog.note.trim() || undefined }
      });
      setCustomsDialog({ open: false, packageId: null, oceCode: '', note: '' });
      toast({ title: 'Dédouané', description: `Colis ${customsDialog.oceCode} dédouané avec succès` });
      fetchShipmentsData();
    } catch (err) {
      toast({ title: t('error'), description: err.response?.data?.detail || t('errorCustomsClearance'), variant: 'destructive' });
    } finally {
      setStatusChanging(false);
    }
  };

  const handleCustomsClearBulk = async () => {
    setStatusChanging(true);
    let successCount = 0;
    let failCount = 0;
    for (const pkgId of customsBulkDialog.packageIds) {
      try {
        await api.put(`/api/packages/${pkgId}/mark-customs-cleared`, null, {
          params: { note: customsBulkDialog.note.trim() || undefined }
        });
        successCount++;
      } catch {
        failCount++;
      }
    }
    setCustomsBulkDialog({ open: false, shipmentId: null, shipmentName: '', packageIds: [], note: '' });
    toast({
      title: 'Dédouanement groupé',
      description: `${successCount} colis dédouané(s)${failCount ? `, ${failCount} échec(s)` : ''}`,
      variant: failCount ? 'destructive' : 'default'
    });
    fetchShipmentsData();
    setStatusChanging(false);
  };

  const openShipmentStatusDialog = (shipment) => {
    setShipmentStatusDialog({
      open: true,
      shipmentId: shipment.id,
      shipmentName: shipment.name,
      currentStatus: shipment.status,
      newStatus: '',
      note: ''
    });
  };

  const openPackageStatusDialog = (pkg) => {
    setPackageStatusDialog({
      open: true,
      packageId: pkg.id,
      oceCode: pkg.oce_code,
      currentStatus: pkg.status,
      newStatus: '',
      note: ''
    });
  };

  if (loading) {
    return (
      <ProfessionalLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-lg text-muted-foreground">{t('loading')}</p>
        </div>
      </ProfessionalLayout>
    );
  }

  // Fonction pour basculer l'expansion d'une carréaison
  const toggleShipment = (shipmentId) => {
    const newExpanded = new Set(expandedShipments);
    if (newExpanded.has(shipmentId)) {
      newExpanded.delete(shipmentId);
    } else {
      newExpanded.add(shipmentId);
    }
    setExpandedShipments(newExpanded);
  };

  // Fonction de recherche dans les carréaisons
  const filterShipments = (shipments, query) => {
    if (!query.trim()) return shipments;
    const q = query.toLowerCase();
    
    return shipments.filter(shipment => {
      return (
        shipment.name.toLowerCase().includes(q) ||
        shipment.origin_country.toLowerCase().includes(q) ||
        shipment.destination_country.toLowerCase().includes(q) ||
        shipment.status.toLowerCase().includes(q) ||
        shipment.partners_data.some(partner => 
          partner.partner_name.toLowerCase().includes(q) ||
          partner.packages.some(pkg => 
            pkg.oce_code.toLowerCase().includes(q) ||
            pkg.sender_name.toLowerCase().includes(q) ||
            pkg.recipient_name.toLowerCase().includes(q)
          )
        )
      );
    });
  };

  // Fonction de filtrage par partenaire dans une carréaison
  const filterPartnerPackages = (partnersData, partnerFilter) => {
    if (partnerFilter === 'all') return partnersData;
    return partnersData.filter(partner => partner.partner_id === partnerFilter);
  };

  // Séparer les carréaisons par statut : la vue active exclut les
  // départs terminés (livrés / annulés) qui basculent dans l'historique.
  const activeShipments = shipmentsData.filter(s => !HISTORY_STATUSES.includes(s.status));
  const historyShipments = shipmentsData.filter(s => HISTORY_STATUSES.includes(s.status));

  // Calculer les statistiques
  const totalShipments = shipmentsData.length;
  const totalPackages = shipmentsData.reduce((sum, s) => sum + s.total_packages, 0);
  const totalWeight = shipmentsData.reduce((sum, s) => sum + s.total_weight, 0);

  // Obtenir tous les partenaires uniques
  const allPartners = new Set();
  shipmentsData.forEach(shipment => {
    shipment.partners_data.forEach(partner => {
      if (partner.partner_id && partner.partner_id !== 'admin') {
        allPartners.add(JSON.stringify({
          id: partner.partner_id,
          name: partner.partner_name
        }));
      }
    });
  });
  const uniquePartners = Array.from(allPartners).map(p => JSON.parse(p));

  // Fonction pour rendre une liste de carréaisons avec recherche
  const renderShipmentsList = (shipmentsList, tabName) => {
    const filteredShipments = filterShipments(shipmentsList, searchQuery);
    
    return (
      <>
        {/* Search Bar for this tab */}
        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t('searchByShipmentPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {filteredShipments.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <Ship className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {searchQuery
                    ? t('noResults')
                    : tabName === 'active' ? t('noShipmentsInProgress') : t('noCompletedShipments')
                  }
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          filteredShipments.map(shipment => {
            const isExpanded = expandedShipments.has(shipment.id);
            const filteredPartners = filterPartnerPackages(shipment.partners_data, selectedPartnerFilter);
            
            return (
            <Card key={shipment.id} className="hover:shadow-md transition-shadow">
              <CardHeader 
                className="cursor-pointer"
                onClick={() => toggleShipment(shipment.id)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <Ship className="h-5 w-5 text-accent" />
                      <div>
                        <CardTitle className="text-xl">{shipment.name}</CardTitle>
                        <CardDescription className="mt-1 flex items-center gap-4 flex-wrap">
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5" />
                            <CountryRoute origin={shipment.origin_country} destination={shipment.destination_country} size="sm" />
                          </span>
                          <span className="flex items-center gap-1 font-semibold text-accent">
                            <Weight className="h-3.5 w-3.5" />
                            {shipment.total_weight} kg
                          </span>
                          <span className="text-muted-foreground">
                            {shipment.total_packages} colis
                          </span>
                          <span className="text-muted-foreground">
                            {shipment.partners_data.length} partenaire(s)
                          </span>
                          {shipment.departure_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3.5 w-3.5" />
                              {new Date(shipment.departure_date).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const eta = getShipmentETA(shipment);
                      if (!eta) return null;
                      const colorMap = { blue: 'bg-accent/10 text-accent', orange: 'bg-accent/10 text-accent', red: 'bg-red-100 text-red-800' };
                      return (
                        <Badge variant="outline" className={`text-xs ${colorMap[eta.color] || ''}`}>
                          {eta.text}
                        </Badge>
                      );
                    })()}
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Diffuser"
                      onClick={(e) => {
                        e.stopPropagation();
                        setBroadcastDialog({ open: true, shipmentId: shipment.id, shipmentName: shipment.name });
                      }}
                    >
                      <Megaphone className="h-4 w-4" />
                    </Button>
                    {(() => {
                      const awaitingPkgs = shipment.partners_data.flatMap(p => p.packages).filter(p => p.status === 'awaiting_customs');
                      if (awaitingPkgs.length === 0) return null;
                      return (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-accent border-accent/30 hover:bg-accent/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCustomsBulkDialog({
                              open: true,
                              shipmentId: shipment.id,
                              shipmentName: shipment.name,
                              packageIds: awaitingPkgs.map(p => p.id),
                              note: ''
                            });
                          }}
                          title="Dédouaner tous les colis en attente"
                        >
                          <ShieldCheck className="h-4 w-4 mr-1" />
                          Dédouaner tout ({awaitingPkgs.length})
                        </Button>
                      );
                    })()}
                    {SHIPMENT_TRANSITIONS[shipment.status]?.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Changer le statut"
                        onClick={(e) => {
                          e.stopPropagation();
                          openShipmentStatusDialog(shipment);
                        }}
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    )}
                    <Badge className={
                      shipment.status === 'completed' ? 'bg-green-500' :
                      shipment.status === 'in_progress' || shipment.status === 'in_transit' ? 'bg-accent' :
                      shipment.status === 'cancelled' ? 'bg-red-500' :
                      shipment.status === 'arrived' ? 'bg-emerald-500' :
                      shipment.status === 'delayed' ? 'bg-amber-500' : 'bg-gray-500'
                    }>
                      {(SHIPMENT_STATUS_LABELS[language] || SHIPMENT_STATUS_LABELS.fr)[shipment.status] || shipment.status}
                    </Badge>
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                </div>
              </CardHeader>

              {isExpanded && (
                <CardContent>
                {shipment.notes && (
                  <div className="mb-4 p-3 bg-accent/10 border border-accent/20 rounded-lg">
                    <p className="text-sm text-accent flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>{shipment.notes}</span>
                    </p>
                  </div>
                )}

                {/* Partner Filter for this shipment */}
                {shipment.partners_data.length > 1 && (
                  <div className="mb-4">
                    <Select value={selectedPartnerFilter} onValueChange={setSelectedPartnerFilter}>
                      <SelectTrigger className="w-64">
                        <SelectValue placeholder={t('filterByPartner')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('allPartners')}</SelectItem>
                        {shipment.partners_data.map(partner => (
                          <SelectItem key={partner.partner_id} value={partner.partner_id}>
                            {partner.partner_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-4">
                  {filteredPartners.map(partner => (
                    <div key={partner.partner_id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <h4 className="font-semibold text-foreground">{partner.partner_name}</h4>
                            <p className="text-sm text-muted-foreground">{partner.partner_email}</p>
                          </div>
                        </div>
                        <Badge variant="outline">
                          {partner.packages.length} colis
                        </Badge>
                      </div>
                      
                      <div className="space-y-2 ml-6">
                        {partner.packages.map(pkg => (
                          <div
                            key={pkg.id}
                            className="p-3 bg-muted rounded border"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="font-medium text-accent">{pkg.oce_code}</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {pkg.sender_name} → {pkg.recipient_name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {pkg.weight} kg | {pkg.description}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge 
                                    className={pkg.is_paid ? 'bg-green-100 text-green-800 text-xs' : 'bg-red-100 text-red-800 text-xs'}
                                  >
                                    {pkg.is_paid ? '✅ Réglé' : '❌ Non réglé'}
                                  </Badge>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setPhotosDialog({ open: true, packageId: pkg.id, oceCode: pkg.oce_code })}
                                  title="Voir les photos"
                                >
                                  <ImageIcon className="h-4 w-4" />
                                </Button>
                                {pkg.status === 'awaiting_customs' && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-accent border-accent/30 hover:bg-accent/10"
                                    onClick={() => setCustomsDialog({ open: true, packageId: pkg.id, oceCode: pkg.oce_code, note: '' })}
                                    title="Dédouaner ce colis"
                                  >
                                    <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                                    Dédouaner
                                  </Button>
                                )}
                                {pkg.status !== 'delivered' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openPackageStatusDialog(pkg)}
                                    title="Changer le statut"
                                  >
                                    <RefreshCw className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                                <PackageStatusBadge status={pkg.status} size="sm" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t text-sm text-muted-foreground space-y-1">
                  {shipment.departure_date && (
                    <p className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Départ: {new Date(shipment.departure_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </p>
                  )}
                  {shipment.expected_arrival_date && (
                    <p className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Arrivée prévue: {new Date(shipment.expected_arrival_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </p>
                  )}
                  <p>Créée le: {new Date(shipment.created_at).toLocaleString('fr-FR')}</p>
                  {shipment.updated_at && shipment.updated_at !== shipment.created_at && (
                    <p className="text-accent flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Modifiée le: {new Date(shipment.updated_at).toLocaleString('fr-FR')}
                    </p>
                  )}
                </div>
              </CardContent>
              )}
            </Card>
          );
          })
        )}
      </>
    );
  };

  return (
    <ProfessionalLayout>
      <SEOHead title={t('seoDashboardTitle')} description={t('seoDashboardDesc')} noindex />
      <div className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Statistics */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          <Card style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #e8ecf4 100%)', borderColor: '#c7d2e8' }}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Départs Totaux</CardTitle>
              <Ship className="h-4 w-4 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalShipments}</div>
              <p className="text-xs text-muted-foreground">Tous voyages</p>
            </CardContent>
          </Card>

          <Card style={{ background: 'linear-gradient(135deg, #fff5f0 0%, #ffe8d6 100%)', borderColor: '#f0c8a8' }}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">En Cours</CardTitle>
              <Ship className="h-4 w-4 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{activeShipments.length}</div>
              <p className="text-xs text-muted-foreground">Planifiées/En cours</p>
            </CardContent>
          </Card>

          <Card style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderColor: '#a7d8b8' }}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Historique</CardTitle>
              <Ship className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{historyShipments.length}</div>
              <p className="text-xs text-muted-foreground">Terminées/Annulées</p>
            </CardContent>
          </Card>

          <Card style={{ background: 'linear-gradient(135deg, #faf5ff 0%, #f0e4ff 100%)', borderColor: '#d4b8f0' }}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Poids Total</CardTitle>
              <Weight className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalWeight.toFixed(1)} kg</div>
              <p className="text-xs text-muted-foreground">Cargo transporté</p>
            </CardContent>
          </Card>

          <Card style={{ background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)', borderColor: '#a5b4fc' }}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Partenaires Actifs</CardTitle>
              <Users className="h-4 w-4 text-indigo-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{uniquePartners.length}</div>
              <p className="text-xs text-muted-foreground">Partenaires impliqués</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="active" className="w-full">
          <TabsList>
            <TabsTrigger value="active">Départs</TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              Historique
              {historyShipments.length > 0 && (
                <Badge variant="secondary" className="text-xs">{historyShipments.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="search">{t('packageSearchTitle')}</TabsTrigger>
          </TabsList>

          <TabsContent value="active" className="space-y-4">
            {renderShipmentsList(activeShipments, 'active')}
          </TabsContent>

          <TabsContent value="history" className="space-y-4">
            {renderShipmentsList(historyShipments, 'completed')}
          </TabsContent>

          <TabsContent value="search">
            <Card>
              <CardHeader>
                <CardTitle>{t('packageSearchTitle')}</CardTitle>
                <CardDescription>
                  {t('packageSearchDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder={t('searchByOcePlaceholder')}
                      value={packageSearchQuery}
                      onChange={(e) => setPackageSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  {packageSearchQuery.length > 0 && packageSearchQuery.length < 3 && (
                    <div className="text-sm text-muted-foreground">
                      {t('minCharsSearch')}
                    </div>
                  )}

                  <div className="space-y-3">
                    {searchResults.length === 0 && packageSearchQuery.length >= 3 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <p>{t('noPackagesFoundFor')} "{packageSearchQuery}"</p>
                      </div>
                    ) : (
                      searchResults.map((result, index) => (
                        <div key={index} className="border rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="font-medium text-lg text-accent">{result.package.oce_code}</p>
                              <p className="text-sm text-muted-foreground mt-1">
                                {result.package.sender_name} → {result.package.recipient_name}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {result.package.weight} kg | {result.package.description}
                              </p>
                              {result.partner && (
                                <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                                  <User className="h-3 w-3" />
                                  Partenaire: {result.partner.name}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <PackageStatusBadge status={result.package.status} size="sm" />
                            </div>
                          </div>
                          
                          {result.shipment ? (
                            <div className="mt-3 p-3 bg-accent/10 border border-accent/20 rounded">
                              <div className="flex items-center gap-2">
                                <Ship className="h-4 w-4 text-accent" />
                                <div>
                                  <p className="font-medium text-accent">{result.shipment.name}</p>
                                  <div className="text-sm text-accent">
                                    <CountryRoute origin={result.shipment.origin_country} destination={result.shipment.destination_country} size="sm" />
                                  </div>
                                  <Badge className="mt-1 text-xs" variant="outline">
                                    {(SHIPMENT_STATUS_LABELS[language] || SHIPMENT_STATUS_LABELS.fr)[result.shipment.status]}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-200 rounded">
                              <p className="text-sm text-yellow-800 flex items-center gap-1">
                                <AlertCircle className="h-4 w-4" />
                                Colis non assigné à un départ
                              </p>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        {/* Package Photos Dialog */}
        <PackagePhotosDialog
          open={photosDialog.open}
          onOpenChange={(open) => setPhotosDialog(prev => ({ ...prev, open }))}
          packageId={photosDialog.packageId}
          oceCode={photosDialog.oceCode}
        />

        {/* Shipment Broadcast Dialog */}
        <ShipmentBroadcastDialog
          open={broadcastDialog.open}
          onOpenChange={(open) => setBroadcastDialog(prev => ({ ...prev, open }))}
          shipmentId={broadcastDialog.shipmentId}
          shipmentName={broadcastDialog.shipmentName}
        />

        {/* Dialog changement statut carréaison */}
        <Dialog
          open={shipmentStatusDialog.open}
          onOpenChange={(open) => {
            if (!open) setShipmentStatusDialog(prev => ({ ...prev, open: false }));
          }}
        >
          <DialogContent className="max-w-[calc(100vw-1rem)] sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('changeShipmentStatus')}</DialogTitle>
              <DialogDescription>
                {shipmentStatusDialog.shipmentName} — {t('currentStatus')} : {(SHIPMENT_STATUS_LABELS[language] || SHIPMENT_STATUS_LABELS.fr)[shipmentStatusDialog.currentStatus] || shipmentStatusDialog.currentStatus}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium mb-1.5 block">{t('newStatus')}</label>
                <Select value={shipmentStatusDialog.newStatus} onValueChange={(v) => setShipmentStatusDialog(prev => ({ ...prev, newStatus: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('selectStatus')} />
                  </SelectTrigger>
                  <SelectContent>
                    {(SHIPMENT_TRANSITIONS[shipmentStatusDialog.currentStatus] || []).map(s => (
                      <SelectItem key={s} value={s}>
                        {(SHIPMENT_STATUS_LABELS[language] || SHIPMENT_STATUS_LABELS.fr)[s] || s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">{t('changeReason')} <span className="text-red-500">*</span></label>
                <Textarea
                  placeholder={t('statusChangeReason')}
                  value={shipmentStatusDialog.note}
                  onChange={(e) => setShipmentStatusDialog(prev => ({ ...prev, note: e.target.value }))}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShipmentStatusDialog(prev => ({ ...prev, open: false }))}
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={handleShipmentStatusChange}
                disabled={!shipmentStatusDialog.newStatus || !shipmentStatusDialog.note.trim() || statusChanging}
              >
                {statusChanging ? t('modifying') : t('confirm')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog changement statut colis */}
        <Dialog
          open={packageStatusDialog.open}
          onOpenChange={(open) => {
            if (!open) setPackageStatusDialog(prev => ({ ...prev, open: false }));
          }}
        >
          <DialogContent className="max-w-[calc(100vw-1rem)] sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('changePackageStatus')}</DialogTitle>
              <DialogDescription>
                {packageStatusDialog.oceCode} — {t('currentStatus')} : {(STATUS_LABELS[language] || STATUS_LABELS.fr)[packageStatusDialog.currentStatus] || packageStatusDialog.currentStatus}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium mb-1.5 block">{t('newStatus')}</label>
                <Select value={packageStatusDialog.newStatus} onValueChange={(v) => setPackageStatusDialog(prev => ({ ...prev, newStatus: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('selectStatus')} />
                  </SelectTrigger>
                  <SelectContent>
                    {ALL_PACKAGE_STATUSES
                      .filter(s => s !== packageStatusDialog.currentStatus)
                      .map(s => (
                        <SelectItem key={s} value={s}>
                          {(STATUS_LABELS[language] || STATUS_LABELS.fr)[s] || s}
                          {!(PACKAGE_TRANSITIONS[packageStatusDialog.currentStatus] || []).includes(s) && ' (admin)'}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">{t('changeReason')} <span className="text-red-500">*</span></label>
                <Textarea
                  placeholder={t('statusChangeReason')}
                  value={packageStatusDialog.note}
                  onChange={(e) => setPackageStatusDialog(prev => ({ ...prev, note: e.target.value }))}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setPackageStatusDialog(prev => ({ ...prev, open: false }))}
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={handlePackageStatusChange}
                disabled={!packageStatusDialog.newStatus || !packageStatusDialog.note.trim() || statusChanging}
              >
                {statusChanging ? t('modifying') : t('confirm')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Dialog dédouanement individuel */}
        <Dialog
          open={customsDialog.open}
          onOpenChange={(open) => { if (!open) setCustomsDialog(prev => ({ ...prev, open: false })); }}
        >
          <DialogContent className="max-w-[calc(100vw-1rem)] sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('customsClearPackage')}</DialogTitle>
              <DialogDescription>
                {t('confirmCustomsClearance')} <strong>{customsDialog.oceCode}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="py-2">
              <label className="text-sm font-medium mb-1.5 block">{t('noteOptional')}</label>
              <Textarea
                placeholder={t('customsDetailsPlaceholder')}
                value={customsDialog.note}
                onChange={(e) => setCustomsDialog(prev => ({ ...prev, note: e.target.value }))}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCustomsDialog(prev => ({ ...prev, open: false }))}>
                {t('cancel')}
              </Button>
              <Button onClick={handleCustomsClearSingle} disabled={statusChanging} className="bg-accent hover:bg-accent/90">
                <ShieldCheck className="h-4 w-4 mr-1" />
                {statusChanging ? t('inProgress') : t('confirmCustoms')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog dédouanement groupé */}
        <Dialog
          open={customsBulkDialog.open}
          onOpenChange={(open) => { if (!open) setCustomsBulkDialog(prev => ({ ...prev, open: false })); }}
        >
          <DialogContent className="max-w-[calc(100vw-1rem)] sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('bulkCustomsClearance')}</DialogTitle>
              <DialogDescription>
                {t('clearNPackagesOf').replace('{n}', customsBulkDialog.packageIds.length)} <strong>{customsBulkDialog.shipmentName}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="py-2">
              <label className="text-sm font-medium mb-1.5 block">{t('noteOptional')}</label>
              <Textarea
                placeholder={t('groupCustomsPlaceholder')}
                value={customsBulkDialog.note}
                onChange={(e) => setCustomsBulkDialog(prev => ({ ...prev, note: e.target.value }))}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCustomsBulkDialog(prev => ({ ...prev, open: false }))}>
                {t('cancel')}
              </Button>
              <Button onClick={handleCustomsClearBulk} disabled={statusChanging} className="bg-accent hover:bg-accent/90">
                <ShieldCheck className="h-4 w-4 mr-1" />
                {statusChanging ? t('inProgress') : t('clearNPackages').replace('{n}', customsBulkDialog.packageIds.length)}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ProfessionalLayout>
  );
};

export default AdminShipmentView;